import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Languages, IndianRupee, Clock, ShieldCheck } from "lucide-react";
import { getTherapistById } from "@/data/therapists";
import { Reveal } from "@/components/Reveal";

export default function TherapistProfile() {
  const { id } = useParams();
  const t = getTherapistById(id);

  if (!t) {
    return (
      <div className="mx-auto max-w-screen-xl px-6 py-32 text-center" data-testid="therapist-not-found">
        <h1 className="font-serif text-4xl">We couldn't find that therapist.</h1>
        <Link to="/find" className="mt-6 inline-flex items-center gap-2 font-semibold text-accent">
          <ArrowLeft className="h-4 w-4" /> Back to all therapists
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 md:py-24 lg:px-24" data-testid={`therapist-profile-${t.id}`}>
      <Link to="/find" data-testid="profile-back" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> All therapists
      </Link>

      <div className="mt-10 grid gap-14 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="overflow-hidden rounded-[2rem] border border-primary/10">
            <img src={t.image} alt={`Portrait of ${t.name}`} className="aspect-[4/5] w-full object-cover" data-testid="profile-image" />
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="font-serif text-5xl font-bold tracking-tight md:text-6xl" data-testid="profile-name">{t.name}</h1>
            <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t.credentials}{t.experience ? ` · ${t.experience} of experience` : ""}
            </p>
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Languages className="h-4 w-4 text-accent" /> {t.languages.join(" · ")}
            </p>

            <p className="mt-8 max-w-2xl text-base leading-relaxed text-foreground/85 md:text-lg" data-testid="profile-about">
              {t.about}
            </p>

            <div className="mt-10 rounded-2xl border border-primary/10 bg-card p-8" data-testid="profile-good-fit">
              <h2 className="font-serif text-2xl font-bold tracking-tight">
                A good fit for you if…
              </h2>
              <ul className="mt-6 space-y-4">
                {t.good_fit.map((g) => (
                  <li key={g} className="flex items-start gap-3 text-sm md:text-base">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 space-y-8">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Areas of support</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {t.expertise.map((e) => (
                    <span key={e} className="rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold">{e}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Approach</h3>
                <p className="mt-4 text-sm leading-relaxed text-foreground/80">{t.approach}</p>
              </div>
            </div>
          </motion.div>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-3xl border border-primary/10 bg-primary p-8 text-primary-foreground md:p-10" data-testid="profile-booking-card">
              <div className="flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="font-serif text-4xl font-bold">₹99</p>
                  <p className="mt-2 text-xs font-bold uppercase tracking-wider text-primary-foreground/70">
                    Your first session
                  </p>
                  <p className="mt-3 flex items-center gap-2 text-xs text-primary-foreground/70">
                    <ShieldCheck className="h-4 w-4" /> Confidential video session · nothing stored
                  </p>
                </div>
                <Link
                  to={`/book/${t.id}`}
                  data-testid="profile-book-cta"
                  className="group flex items-center gap-2 rounded-full bg-primary-foreground px-7 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                >
                  Book with {t.first_name}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Not sure {t.first_name} is the one?{" "}
              <Link to="/find" className="font-semibold text-accent underline-offset-4 hover:underline" data-testid="profile-explore-others">
                Explore other therapists
              </Link>{" "}
              — you can switch anytime, no questions asked.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
